import { secondAxios } from "../../../../../../../api/axios";
import { getDrawingData } from "../../../../../../../pages/client/AllEquipments/CommonFeatures/MCS3/BOMTab/DrawingRegisterTab/api";
import { getAllData } from "../../../../../../../pages/client/Ams/PV3/api";

const clientName = process.env.REACT_APP_CLIENT_NAME;

// ─────────────────────────────────────────────────────────────
// Normalized material row (common shape for drawings + estimations)
// ─────────────────────────────────────────────────────────────
// { key, uid, description, quantity, dimension, weight, cost, schedule }

const parseMaybeJson = (value) => {
  if (value && typeof value === "object") return value;
  if (typeof value !== "string" || !value.trim()) return {};
  try {
    return JSON.parse(value) ?? {};
  } catch {
    return {};
  }
};

// Drawing BOM material -> normalized row.
const normalizeDrawingMaterial = (m, index) => ({
  key: String(
    m?.tagNumber || m?.item || m?.UID || m?.id || `drawing-${index}`,
  ),
  uid: m?.UID || m?.materialCode || String(m?.tagNumber ?? m?.item ?? ""),
  description:
    m?.description ||
    [m?.component, m?.size].filter(Boolean).join(" ") ||
    "Untitled",
  quantity: m?.quantity ?? null,
  dimension: m?.length ?? null,
  weight: m?.totalWtInKgs ?? m?.weight ?? null,
  cost: m?.totalCost ?? m?.budgetedCost ?? m?.cost ?? null,
  schedule: m?.schedule ?? null,
});

// PV Estimation (DataGridView8) row -> normalized row. Qty may carry a
// dimension ("A x B"), same parsing as the Estimation tab's BOM builder.
const normalizePvEstimationRow = (row, index) => {
  const qtyStr = String(row?.Qty ?? "").trim();
  let quantity = row?.Qty ?? null;
  let dimension = null;
  if (/\bx\b/i.test(qtyStr)) {
    const parts = qtyStr.split(/\s*x\s*/i).filter(Boolean);
    dimension = parts.length >= 2 ? `${parts[0]} x ${parts[1]}` : qtyStr;
    quantity = 1;
  }
  return {
    key: String(row?.UID || row?.Description || `pv-est-${index}`),
    uid: row?.UID || row?.Description || "",
    description: row?.Description || "Untitled",
    quantity,
    dimension,
    weight: row?.["Measure/Wt"] ?? null,
    cost: row?.["Material Cost"] ?? row?.["Material Cost/ Unit"] ?? null,
    schedule: null,
  };
};

// Piping/Structural estimation Summary row -> normalized row.
const normalizePipStrucEstimationRow = (row, index) => {
  const qty = row?.procuredQty ?? null;
  const unitCost = row?.unitCost ?? null;
  const cost =
    qty !== null && qty !== "" && unitCost !== null && unitCost !== ""
      ? Number(qty) * Number(unitCost)
      : (unitCost ?? null);
  const description =
    row?.MaterialDescription ||
    row?.Description ||
    row?.["Material Description"] ||
    "Untitled";
  return {
    key: String(row?.UID || description || `est-${index}`),
    uid: row?.UID || description,
    description,
    quantity: qty,
    dimension: null,
    weight: null,
    cost: Number.isNaN(cost) ? null : cost,
    schedule: null,
  };
};

const fetchPvEstimationMaterials = async (target) => {
  const res = await getAllData(target.projectId);
  const entry = res?.all?.inputData?.[target.revision];
  const rows = entry?.estimation?.DataGridView8;
  return (Array.isArray(rows) ? rows : []).map(normalizePvEstimationRow);
};

const fetchPipStrucEstimationMaterials = async (target) => {
  const res = await secondAxios.get(
    `/project/plant/${target.plantId}/${clientName}`,
  );
  const items = Array.isArray(res.data) ? res.data : [];
  const wbs = items.find((w) => String(w?.id) === String(target.projectId));
  const revisions = Array.isArray(wbs?.revisions) ? wbs.revisions : [];
  const rev = revisions.find(
    (r) => String(r?.name) === String(target.revision),
  );
  const table = parseMaybeJson(rev?.data)?.estimationTable;
  const est0 = Array.isArray(table)
    ? table[0]
    : (table?.["0"] ?? table?.[0]);
  const summary = est0?.Summary;
  const rows = Array.isArray(summary) ? summary : [];
  return rows.map(normalizePipStrucEstimationRow);
};

/**
 * Load one compare side's materials as normalized rows.
 * - 2D Drawing  -> GET drawing/:id -> .materials
 * - PV Estimation -> PV all-data -> inputData[rev].estimation.DataGridView8
 * - Pip/Struc Estimation -> project revisions -> estimationTable[0].Summary
 */
export async function fetchSideMaterials(target) {
  if (!target) return [];
  if (target.kind === "drawing" && target.drawingId) {
    const drawing = await getDrawingData(target.drawingId);
    const materials = Array.isArray(drawing?.materials)
      ? drawing.materials
      : [];
    return materials.map(normalizeDrawingMaterial);
  }
  if (target.kind === "pv" && target.projectId && target.revision != null) {
    if (target.topTabLabel) return fetchPipStrucEstimationMaterials(target);
    if (target.subTabLabel === "Estimation") {
      return fetchPvEstimationMaterials(target);
    }
  }
  return [];
}

/** Stable identity for a compare side (used for fetching + effect keys). */
export function sideKey(selection) {
  if (!selection) return "";
  if (selection.kind === "drawing" && selection.drawingId) {
    return `drawing:${selection.drawingId}`;
  }
  if (
    selection.kind === "pv" &&
    selection.projectId &&
    selection.revision != null
  ) {
    return `estimation:${selection.projectId}:${selection.revision}`;
  }
  return "";
}
